import { useCallback, useMemo } from 'react';

import {
  ILockInfo,
  LockContext,
  useLock,
} from '@work4all/components/lib/hooks';

interface LockOverrideProps {
  forceLock?: boolean;
  lockReason?: string;
  lockReasonTitle?: string;
  children?: React.ReactNode;
  isLockedByFeature?: (feature: string, originalLocked: boolean) => boolean;
}

/**
 * Allows you to override the LockContext to force all input into a disabled
 * state.
 */
export function LockOverride({
  forceLock = false,
  lockReason,
  lockReasonTitle,
  children,
  isLockedByFeature: isLockedByFeatureFromProps,
}: LockOverrideProps) {
  const ctx = useLock();

  const { locked: originalLocked, lock, unlock, user } = ctx;

  const isLockedByFeature = useCallback(
    (feature: string) => {
      return isLockedByFeatureFromProps(feature, originalLocked);
    },
    [isLockedByFeatureFromProps, originalLocked]
  );

  const locked = forceLock || originalLocked;

  const override = useMemo<ILockInfo>(() => {
    return {
      locked,
      lock,
      unlock,
      user,
      lockReason,
      lockReasonTitle,
      isLockedByFeature,
    };
  }, [
    locked,
    lock,
    unlock,
    user,
    lockReason,
    lockReasonTitle,
    isLockedByFeature,
  ]);

  return (
    <LockContext.Provider value={override}>{children}</LockContext.Provider>
  );
}
